/**
 * useIncidentListState — URL search params as the single source of truth for
 * the Incidents list (filters + paging), with per-tab memory.
 *
 * Why the URL: the list unmounts whenever an analyst opens an incident, so
 * component state dies. Keeping everything in the query string makes the
 * browser Back button restore the view for free (every change uses
 * `replace: true`, so the history entry for the list always carries the
 * current params without typing in the search box spamming history).
 *
 * Why sessionStorage: "Back to Incidents" links and the sidebar nav item are
 * hard links to bare `/incidents`. When the list is opened with no query
 * string and this tab has a remembered view, the URL is re-canonicalised to
 * it. Storage is per tab, so two tabs never fight over the same filters.
 *
 * Defaults serialise to NO params so the canonical default URL stays
 * `/incidents`.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { QuickFilter } from '../components/incidents/IncidentSidebar';
import type { Severity, PlatformStatus } from '../data/types';

export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

const STORAGE_KEY = 'axn-analyst:incident-list:v1';

const QUICK_FILTERS: readonly QuickFilter[] = ['all_open', 'my_tickets', 'unassigned', 'closed', 'all'];
const SEVERITIES: readonly Severity[] = ['critical', 'high', 'medium', 'low', 'informational'];
const PLATFORM_STATUSES: readonly PlatformStatus[] = ['new', 'acknowledged', 'investigating', 'closed', 'reopened'];

export interface IncidentListState {
  search: string;
  quick: QuickFilter;
  tenants: string[];
  severities: Severity[];
  statuses: PlatformStatus[];
  dateFrom: string;
  dateTo: string;
  /** Hide incidents Defender resolved before any analyst touched them (AB#133). */
  hideAutoResolved: boolean;
  /** 1-based */
  page: number;
  pageSize: PageSize;
}

/** Everything that narrows the list beyond the quick-filter queue itself. */
export function resetDetailFilters(state: IncidentListState): void {
  state.search = '';
  state.tenants = [];
  state.severities = [];
  state.statuses = [];
  state.dateFrom = '';
  state.dateTo = '';
  state.hideAutoResolved = false;
}

export interface ActiveFilterChip {
  key: string;
  label: string;
}

/**
 * Human-readable chips for every filter narrowing the current view, so a
 * filtered list can never masquerade as the whole queue (AB#137). The quick
 * filter itself is not a chip — the highlighted tab already shows it.
 */
export function describeActiveFilters(
  state: IncidentListState,
  tenantNames: Record<string, string> = {}
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  if (state.search) chips.push({ key: 'search', label: `Search: "${state.search}"` });
  if (state.severities.length) {
    chips.push({ key: 'severities', label: `Severity: ${state.severities.join(', ')}` });
  }
  if (state.statuses.length) {
    chips.push({ key: 'statuses', label: `Status: ${state.statuses.join(', ')}` });
  }
  if (state.tenants.length) {
    const names = state.tenants.map((id) => tenantNames[id] ?? id);
    chips.push({ key: 'tenants', label: `Tenant: ${names.join(', ')}` });
  }
  if (state.dateFrom) chips.push({ key: 'dateFrom', label: `From ${state.dateFrom}` });
  if (state.dateTo) chips.push({ key: 'dateTo', label: `To ${state.dateTo}` });
  if (state.hideAutoResolved) chips.push({ key: 'hideAutoResolved', label: 'Hiding auto-resolved' });
  return chips;
}

// ============================================================================
// Pure codec
// ============================================================================

function csv(params: URLSearchParams, key: string): string[] {
  return (params.get(key) ?? '').split(',').filter(Boolean);
}

function pickAllowed<T extends string>(values: string[], allowed: readonly T[]): T[] {
  return values.filter((v): v is T => (allowed as readonly string[]).includes(v));
}

export function parseState(params: URLSearchParams): IncidentListState {
  const severities = pickAllowed(csv(params, 'severities'), SEVERITIES);
  const statuses = pickAllowed(csv(params, 'statuses'), PLATFORM_STATUSES);

  const rawQuick = params.get('quick');
  let quick: QuickFilter = 'all_open';
  if (rawQuick && (QUICK_FILTERS as readonly string[]).includes(rawQuick)) {
    quick = rawQuick as QuickFilter;
  } else if (!rawQuick && (severities.length > 0 || statuses.length > 0)) {
    // Legacy deep links (`/incidents?severities=high`) carried no quick
    // filter and meant "custom filter mode".
    quick = 'all';
  }

  const rawPage = Number.parseInt(params.get('page') ?? '', 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

  const rawSize = Number.parseInt(params.get('pageSize') ?? '', 10);
  const pageSize = (PAGE_SIZES as readonly number[]).includes(rawSize)
    ? (rawSize as PageSize)
    : DEFAULT_PAGE_SIZE;

  return {
    search: params.get('search') ?? '',
    quick,
    tenants: csv(params, 'tenants'),
    severities,
    statuses,
    dateFrom: params.get('dateFrom') ?? '',
    dateTo: params.get('dateTo') ?? '',
    hideAutoResolved: params.get('hideAutoResolved') === '1',
    page,
    pageSize,
  };
}

export function serializeState(state: IncidentListState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.search) params.set('search', state.search);
  // Emit `quick` whenever severities/statuses are present, even if it is the
  // default: otherwise `all_open + severities` would re-parse as `all`.
  const hasDetailFilters = state.severities.length > 0 || state.statuses.length > 0;
  if (state.quick !== 'all_open' || hasDetailFilters) params.set('quick', state.quick);
  if (state.tenants.length) params.set('tenants', state.tenants.join(','));
  if (state.severities.length) params.set('severities', state.severities.join(','));
  if (state.statuses.length) params.set('statuses', state.statuses.join(','));
  if (state.dateFrom) params.set('dateFrom', state.dateFrom);
  if (state.dateTo) params.set('dateTo', state.dateTo);
  if (state.hideAutoResolved) params.set('hideAutoResolved', '1');
  if (state.page > 1) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(state.pageSize));
  return params;
}

// ============================================================================
// Storage (per tab; every access guarded — Safari private mode throws)
// ============================================================================

function readStored(): string {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeStored(value: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

function clearStored(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ============================================================================
// Hook
// ============================================================================

export interface IncidentListStateApi {
  state: IncidentListState;
  /** Canonical param string — stable identity key for effects. */
  key: string;
  setSearch: (value: string) => void;
  setQuickFilter: (value: QuickFilter) => void;
  setTenants: (value: string[]) => void;
  setSeverities: (value: Severity[]) => void;
  setStatuses: (value: PlatformStatus[]) => void;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  setHideAutoResolved: (value: boolean) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  /** Reset to the default view and forget the remembered one. */
  clear: () => void;
}

export function useIncidentListState(): IncidentListStateApi {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKey = searchParams.toString();

  // A bare URL with a remembered view behaves as if the URL already had it,
  // so the first render (and therefore the first fetch) uses the restored
  // filters — no default-then-restored double fetch.
  const effectiveKey = urlKey === '' ? readStored() : urlKey;
  const state = useMemo(() => parseState(new URLSearchParams(effectiveKey)), [effectiveKey]);

  // `setSearchParams` is recreated on every URL change; route it through a
  // ref so the setters below keep a stable identity.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  // What the URL *will* be after pending updates. The functional form of
  // setSearchParams reads the render-time value under BrowserRouter, so two
  // setter calls in one event handler (the sidebar toggles a severity and
  // then switches the quick filter) would clobber each other without this.
  const latestRef = useRef(effectiveKey);
  useEffect(() => {
    latestRef.current = effectiveKey;
  }, [effectiveKey]);

  // Keep storage in sync with the URL; re-canonicalise a bare URL from storage.
  useEffect(() => {
    if (urlKey === '') {
      const stored = readStored();
      if (stored) {
        setSearchParamsRef.current(new URLSearchParams(stored), { replace: true });
      }
      return;
    }
    writeStored(urlKey);
  }, [urlKey]);

  const update = useCallback((mutate: (draft: IncidentListState) => void) => {
    const draft = parseState(new URLSearchParams(latestRef.current));
    mutate(draft);
    const next = serializeState(draft);
    const nextKey = next.toString();
    latestRef.current = nextKey;
    // Keep storage in step with what the analyst just chose. The default view
    // serialises to an EMPTY string, and the sync effect treats a bare URL as
    // "arrived from outside — restore the remembered view". Without clearing
    // storage here, selecting All Open after My Tickets would immediately be
    // undone by that restore (live regression, 2026-09-07).
    if (nextKey === '') {
      clearStored();
    } else {
      writeStored(nextKey);
    }
    setSearchParamsRef.current(next, { replace: true });
  }, []);

  const setSearch = useCallback((value: string) => update((d) => { d.search = value; d.page = 1; }), [update]);
  const setQuickFilter = useCallback((value: QuickFilter) => update((d) => {
    // "All Open" means the whole open queue. A search term or detail filter
    // carried in from another view used to survive the switch with nothing on
    // screen saying so, and a new incident outside it was simply invisible
    // (AB#137, reported from live SOC use). Reset rather than warn: the tab's
    // one job is to show everything.
    if (value === 'all_open') resetDetailFilters(d);
    d.quick = value;
    d.page = 1;
  }), [update]);
  const setTenants = useCallback((value: string[]) => update((d) => { d.tenants = value; d.page = 1; }), [update]);
  const setSeverities = useCallback((value: Severity[]) => update((d) => { d.severities = value; d.page = 1; }), [update]);
  const setStatuses = useCallback((value: PlatformStatus[]) => update((d) => { d.statuses = value; d.page = 1; }), [update]);
  const setDateFrom = useCallback((value: string) => update((d) => { d.dateFrom = value; d.page = 1; }), [update]);
  const setDateTo = useCallback((value: string) => update((d) => { d.dateTo = value; d.page = 1; }), [update]);
  const setHideAutoResolved = useCallback((value: boolean) => update((d) => { d.hideAutoResolved = value; d.page = 1; }), [update]);
  const setPage = useCallback((page: number) => update((d) => { d.page = Math.max(1, Math.floor(page)); }), [update]);
  const setPageSize = useCallback((size: number) => update((d) => {
    d.pageSize = (PAGE_SIZES as readonly number[]).includes(size) ? (size as PageSize) : DEFAULT_PAGE_SIZE;
    d.page = 1;
  }), [update]);

  const clear = useCallback(() => {
    // Remove storage BEFORE navigating, or the sync effect restores it.
    clearStored();
    latestRef.current = '';
    setSearchParamsRef.current(new URLSearchParams(), { replace: true });
  }, []);

  return {
    state,
    key: effectiveKey,
    setSearch,
    setQuickFilter,
    setTenants,
    setSeverities,
    setStatuses,
    setDateFrom,
    setDateTo,
    setHideAutoResolved,
    setPage,
    setPageSize,
    clear,
  };
}
